import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { AppLayout, GlassCard, PageHeader, LoadingSpinner } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Users, Search, Trash2, RefreshCw, Shield, Mail, Calendar, CreditCard, ChevronLeft, ChevronRight, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { showFriendlyError } from '../utils/friendlyErrors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_STYLES = {
  super_admin: { label: 'Super Admin', bg: 'bg-red-500/20 text-red-300' },
  parent: { label: 'Parent', bg: 'bg-blue-500/20 text-blue-300' },
  student: { label: 'Student', bg: 'bg-emerald-500/20 text-emerald-300' },
  professional: { label: 'Professional', bg: 'bg-amber-500/20 text-amber-300' },
};

const TIER_STYLES = {
  plus: { label: 'Plus', color: 'text-cyan-400' },
  career_reinvention: { label: 'Career Reinvention', color: 'text-amber-400' },
  family: { label: 'Family', color: 'text-blue-400' },
  elite: { label: 'Elite', color: 'text-amber-400' },
  free: { label: 'Free', color: 'text-slate-300' },
};

export default function UserManagementPage() {
  const { getAuthHeader } = useAuth();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [page, setPage] = useState(0);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const limit = 20;

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit, skip: page * limit });
      if (search) params.set('search', search);
      if (roleFilter) params.set('role', roleFilter);
      const res = await axios.get(`${API}/admin/users?${params}`, getAuthHeader());
      setUsers(res.data.users || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, search, roleFilter, page]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleDelete = async (userId, email) => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/users/${userId}`, getAuthHeader());
      toast.success(`Deleted ${email}`);
      setDeleteConfirm(null);
      fetchUsers();
    } catch (err) {
      showFriendlyError(err, { fallback: 'Failed to delete user' });
    } finally {
      setDeleting(false);
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <AppLayout>
      <PageHeader title="User Management" description={`${total} total users`} />

      {/* Filters */}
      <div className="flex items-center gap-3 mb-6 flex-wrap" data-testid="user-filters">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
          <Input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            placeholder="Search by name or email..."
            className="pl-9 bg-slate-800 border-slate-700"
            data-testid="user-search"
          />
        </div>
        <select
          value={roleFilter}
          onChange={(e) => { setRoleFilter(e.target.value); setPage(0); }}
          className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white"
          data-testid="role-filter"
        >
          <option value="">All Roles</option>
          <option value="parent">Parents</option>
          <option value="student">Students</option>
          <option value="professional">Professionals</option>
          <option value="super_admin">Admins</option>
        </select>
        <Button variant="outline" size="sm" onClick={fetchUsers} className="border-slate-700 text-slate-300" data-testid="refresh-users">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Users Table */}
      <GlassCard className="overflow-hidden p-0">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="users-table">
            <thead>
              <tr className="border-b border-slate-700 text-left">
                <th className="px-4 py-3 text-xs font-medium text-slate-300 uppercase">User</th>
                <th className="px-4 py-3 text-xs font-medium text-slate-300 uppercase">Role</th>
                <th className="px-4 py-3 text-xs font-medium text-slate-300 uppercase">Plan</th>
                <th className="px-4 py-3 text-xs font-medium text-slate-300 uppercase">Joined</th>
                <th className="px-4 py-3 text-xs font-medium text-slate-300 uppercase">Status</th>
                <th className="px-4 py-3 text-xs font-medium text-slate-300 uppercase text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="text-center py-12"><LoadingSpinner /></td></tr>
              ) : users.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-12 text-slate-300">No users found</td></tr>
              ) : users.map(user => {
                const role = ROLE_STYLES[user.role] || { label: user.role, bg: 'bg-slate-500/20 text-slate-300' };
                const tier = TIER_STYLES[user.subscription_tier] || TIER_STYLES.free;
                return (
                  <tr key={user.id} className="border-b border-slate-800 hover:bg-slate-800/50 transition-colors" data-testid={`user-row-${user.id}`}>
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-white font-medium text-sm">{user.full_name || 'No name'}</p>
                        <p className="text-slate-300 text-xs flex items-center gap-1">
                          <Mail className="w-3 h-3" /> {user.email}
                        </p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${role.bg}`}>
                        {role.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-sm font-medium ${tier.color}`}>{tier.label}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-slate-300 text-xs flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        user.status === 'active' ? 'bg-emerald-500/20 text-emerald-300' : 'bg-slate-500/20 text-slate-300'
                      }`}>
                        {user.status || 'active'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {user.role !== 'super_admin' ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteConfirm(user)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          data-testid={`delete-user-${user.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      ) : (
                        <Shield className="w-4 h-4 text-amber-400 inline-block" title="Protected" />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-700">
            <p className="text-xs text-slate-300">
              Showing {page * limit + 1}-{Math.min((page + 1) * limit, total)} of {total}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                className="border-slate-700"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => p + 1)}
                disabled={page >= totalPages - 1}
                className="border-slate-700"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </GlassCard>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0a1729]/70 backdrop-blur-sm p-4" data-testid="delete-confirm-modal">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-400" />
              </div>
              <h3 className="text-lg font-semibold text-white">Delete User</h3>
            </div>
            <p className="text-slate-300 text-sm mb-2">
              Are you sure you want to delete this user and ALL their data?
            </p>
            <div className="p-3 bg-slate-800 rounded-lg mb-4">
              <p className="text-white font-medium text-sm">{deleteConfirm.full_name || 'No name'}</p>
              <p className="text-slate-300 text-xs">{deleteConfirm.email}</p>
              <p className="text-slate-300 text-xs capitalize">{deleteConfirm.role} - {deleteConfirm.subscription_tier || 'free'}</p>
            </div>
            <p className="text-red-400 text-xs mb-4">
              This will permanently delete their account, family links, documents, quiz results, payment records, and all associated data. This cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setDeleteConfirm(null)} className="border-slate-600 text-slate-300">
                Cancel
              </Button>
              <Button
                onClick={() => handleDelete(deleteConfirm.id, deleteConfirm.email)}
                disabled={deleting}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-delete-btn"
              >
                {deleting ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
                Delete User
              </Button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
